import classNames from 'classnames';
import * as Immutable from 'immutable';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import Tooltip from 'components/Tooltip';
import * as Constants from 'const';
import { ProjectType } from 'const';
import { updateActiveSurfaceId } from 'containers/Project/actions';
import { setScreenFormat } from 'containers/Surfaces/actions';
import { ArtboardStylesMap, MasterScreen, ScreenMap } from 'models';
import { EntityTypeIcon } from './components/EntityTypeIcon';
import ScreenFormatType from './components/ScreenFormatType';
import { ScreenMenu } from './components/ScreenMenu';
import { ScreenName } from './components/ScreenName';
import { ScreenNumber } from './components/ScreenNumber';
import ScreenPreview from './components/ScreenPreview';
import ScreenType from './components/ScreenType';
import styles from './styles.module.scss';
import { useScreenDnD } from './useScreenDnD';

type Props = {
  screenId: string;
  artboardStyles: ArtboardStylesMap;
  entityType: string;
  isActive: boolean;
  isDeletingDisabled: boolean;
  isOver: boolean;
  position: number;
  projectType: ProjectType;
  screen: ScreenMap;
  screenDefinitions: MasterScreen.ScreenDefinitionsMap;
  screenNames: Immutable.List<string>;
  showIconComponent: boolean;
  toggleOverState: (isOver: boolean) => void;
  updateHotspotPosition: (position: number) => void;
  isReadOnly?: boolean;
  dataTestId?: string;
};

const ScreensPanelItem: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const {
    screenId,
    isActive,
    isDeletingDisabled,
    position,
    projectType,
    screenDefinitions,
    showIconComponent,
    screen,
    artboardStyles,
    isReadOnly,
    dataTestId = 'screen0',
    updateHotspotPosition,
    screenNames,
    isOver,
    toggleOverState,
    entityType,
  } = props;

  const {
    areScreensResizable,
    allowScreenSettings,
    allowThumbnails,
  } = Constants.ProjectsConfig[projectType];

  const initialScreenName = screen.get('name');
  const thumbnail = screen.get('thumbnail');
  const formatType = screen.get('formatType');
  const height = artboardStyles.get('height');
  const width = artboardStyles.get('width');

  const [isShowTooltip, setIsShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipParent, setTooltipParent] = useState<React.RefObject<HTMLElement>>();

  const panelItemRef = useRef<HTMLDivElement>(null);

  const { isDragging } = useScreenDnD({ screenId, panelItemRef, position, isOver, toggleOverState, updateHotspotPosition });

  function toggleTooltip(
    element?: React.RefObject<HTMLElement>,
    show = false,
    text = '',
  ): void {
    setIsShowTooltip(show);
    setTooltipParent(element);
    setTooltipText(text);
  }

  const onScreenClick = (): void => {
    if (isActive) {
      return;
    }
    dispatch(updateActiveSurfaceId(screenId));
  };

  const onScreenFormatChange = (screenFormatType: Constants.ScreenFormatType): void => {
    dispatch(setScreenFormat({ screenId, ...Constants.PredefinedScreenFormatByType[screenFormatType] }));
  };

  return (
    <div
      ref={panelItemRef}
      key={screenId}
      onClick={onScreenClick}
      className={classNames(styles.ScreensPanelItem, {
        [styles.ScreensPanelItem__active]: isActive,
        [styles.ScreensPanelItem__dragging]: isDragging,
      })}
      style={{ order: position }}
      data-testid={dataTestId}
    >
      <div className={styles.dragDots} data-testid={`${dataTestId}-dragDots`}>
        <Icon type={IconType.DRAG_DOTS} size="sm" color="grey" dataTestId={`${dataTestId}-dragDots`} />
      </div>
      <div className={styles.screenInfo} data-testid={`${dataTestId}-info`}>
        <ScreenPreview
          thumbnail={thumbnail}
          allowThumbnails={allowThumbnails}
          dataTestId={dataTestId}
        />
        <div className={styles.screenDescription} data-testid={`${dataTestId}-info-description`}>
          <div className={styles.screenTitleBar} data-testid={`${dataTestId}-info-description-title`}>
            <ScreenName
              screenNames={screenNames}
              screenId={screenId}
              toggleTooltip={toggleTooltip}
              initialScreenName={initialScreenName}
              dataTestId={dataTestId}
              isReadOnly={isReadOnly}
            />
            <ScreenMenu
              isReadOnly={isReadOnly}
              screenId={screenId}
              dataTestId={dataTestId}
              toggleTooltip={toggleTooltip}
              isDeletingDisabled={isDeletingDisabled}
              allowScreenSettings={allowScreenSettings}
            />
          </div>
          {
            areScreensResizable
              ?
              <ScreenFormatType
                customHeight={height}
                customWidth={width}
                formatType={formatType}
                onFormateChange={onScreenFormatChange}
                isTooltipDisabled={false}
                disabled={isReadOnly}
                showCustomFormatOnlyWhenItIsActive={true}
                dataTestId={`${dataTestId}-info-description-screenFormatType`}
              />
              :
              <ScreenType
                screen={screen}
                disabled={isReadOnly}
                screenDefinitions={screenDefinitions}
                dataTestId={`${dataTestId}-info-description-screenType`}
              />
          }
        </div>
        <EntityTypeIcon entityType={entityType} showIconComponent={showIconComponent} dataTestId={dataTestId}/>
      </div>
      <Tooltip
        arrow={false}
        className={styles.tooltip}
        parent={tooltipParent}
        show={isShowTooltip}
        text={tooltipText}
        dataTestId={`${dataTestId}-tooltip`}
      />
      <ScreenNumber showIconComponent={showIconComponent} position={position} dataTestId={`${dataTestId}-number`}/>
    </div>
  );
};

export default ScreensPanelItem;
